<template>
  <div class="d-flex flex-column min-vh-100 overflow-hidden">
    <!-- Header -->
    <header class="row align-items-center p-2 m-0 w-100 sticky-top" :style="headerStyle">
      <div class="col d-flex align-items-center">
        <!-- Hide headerBrand on phones -->
        <router-link to="/" class="text-white text-decoration-none fs-4">{{ headerBrand }}</router-link>
      </div>
      <!-- Hide title on phones -->
      <div class="col-5 text-center text-white d-none d-sm-block">
        <span>{{ title }}</span>
      </div>
      <div class="col text-end">
        <div v-if="timeToExpire > 0">
          <!-- Impersonation Buttons, hidden on phones -->
          <button v-if="isTeacher && !hideImpersonation" class="btn btn-outline-light me-2" @click="openImpersonateModal">
            Impersonate
          </button>
          <button v-if="isImpersonating && !hideImpersonation" class="btn btn-warning me-2" @click="stopImpersonationAction">
            Stop Impersonation
          </button>
          <!-- Dark Mode Toggle for desktop -->
          <button class="btn btn-outline-light me-2 d-none d-sm-inline" @click="toggleTheme" v-tooltip="'Switch Dark/Light mode'">
            <span v-if="theme === 'light'">🌙</span>
            <span v-else>☀️</span>
          </button>
          <!-- UCO Display, hidden on phones -->
          <span class="text-white me-2 d-none d-sm-inline">UCO: {{ userUco }}</span>
          <!-- Logout Button for desktop -->
          <button class="btn btn-secondary d-none d-sm-inline" @click="logout">
           <i class="bi bi-box-arrow-right"></i> Logout
          </button>
        </div>
        <div v-else>
          <!-- Login Link with tooltip -->
          <router-link to="/login" class="btn btn-primary">Login</router-link>
        </div>
      </div>
      <!-- Mobile Menu Toggle Button on right -->
      <div class="col-auto d-block d-sm-none text-end" v-if="!hideNavigation">
        <button class="btn btn-outline-light" type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasMenu" aria-controls="offcanvasMenu" aria-label="Toggle navigation">
          <i class="bi bi-list"></i>
        </button>
      </div>
    </header>

    <!-- Offcanvas Mobile Menu -->
    <div v-if="!hideNavigation" class="offcanvas offcanvas-end d-block d-sm-none" tabindex="-1" id="offcanvasMenu" aria-labelledby="offcanvasMenuLabel">
      <div class="offcanvas-header">
        <h5 class="offcanvas-title" id="offcanvasMenuLabel">{{ headerBrand }}</h5>
        <button type="button" class="btn-close" data-bs-dismiss="offcanvas" aria-label="Close"></button>
      </div>
      <div class="offcanvas-body">
        <div class="mb-2">UCO: {{ userUco }}</div>
        <nav class="nav nav-pills flex-column">
          <router-link to="/" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-house-door"></i>  Home
          </router-link>
          <router-link to="/homeworks" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-journal"></i>  Homeworks
          </router-link>
          <router-link to="/evaluations" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-clipboard-check"></i>  Evaluations
          </router-link>
          <router-link v-if="!isTeacher" to="/ask-ai" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-robot"></i>  Ask AI
          </router-link>
          <router-link v-if="!isTeacher" to="/materials" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-journal-text"></i>  Course Materials
          </router-link>
          <router-link v-if="isTeacher" to="/teacher-ask-ai" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-robot"></i>  Ask AI
          </router-link>
          <router-link v-if="isTeacher" to="/queue" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-list"></i>  Queue & Workers
          </router-link>
          <router-link v-if="isTeacher" to="/global_files" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-folder"></i>  Global Files
          </router-link>
          <router-link v-if="isTeacher" to="/course_actions" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-gear"></i>  Course Actions
          </router-link>
          <router-link v-if="isTeacher" to="/semesters" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-calendar3"></i>  Manage Semesters
          </router-link>
          <router-link v-if="isTeacher" to="/course_materials" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-journal-text"></i>  Course Materials
          </router-link>
          <router-link to="/competition" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-trophy"></i>  Competition
          </router-link>
          <router-link to="/help" class="nav-link border mb-2" active-class="active" @click="closeOffcanvasMenu">
            <i class="bi bi-question-circle"></i>  Help
          </router-link>
        </nav>
        <!-- Dark Mode Toggle and Logout/Login for mobile -->
        <div class="mt-3">
          <button class="btn btn-outline-light w-100 mb-2" @click="toggleTheme" v-tooltip="'Switch Dark/Light mode'">
            <span v-if="theme === 'light'">🌙</span>
            <span v-else>☀️</span>
          </button>
          <button v-if="timeToExpire > 0" class="btn btn-secondary w-100" @click="logout">
            <i class="bi bi-box-arrow-right"></i> Logout
          </button>
          <router-link v-else to="/login" class="btn btn-primary w-100" @click="closeOffcanvasMenu">
            <i class="bi bi-box-arrow-in-right"></i> Login
          </router-link>
        </div>
      </div>
    </div>

    <!-- Impersonation Modal -->
    <div class="modal fade" id="impersonateModal" tabindex="-1">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Impersonate Student</h5>
            <button type="button" class="btn-close" @click="closeImpersonateModal"></button>
          </div>
          <div class="modal-body">
            <input v-model="impersonateUco" type="text" class="form-control" placeholder="Enter Student UCO" />
          </div>
          <div class="modal-footer">
            <button class="btn btn-secondary" @click="closeImpersonateModal">Cancel</button>
            <button class="btn btn-primary" @click="impersonate">Impersonate</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content Area -->
    <div class="container-fluid flex-fill overflow-hidden">
      <div class="row h-100">
        <!-- Desktop Sidebar (NavBar) -->
        <aside class="col-md-2 col-sm-3 p-0 border-end d-none d-sm-block">
          <nav v-if="!hideNavigation" class="nav nav-pills flex-column p-2 h-100">
            <router-link to="/" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-house-door"></i>  Home
            </router-link>
            <router-link to="/homeworks" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-journal"></i>  Homeworks
            </router-link>
            <router-link to="/evaluations" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-clipboard-check"></i>  Evaluations
            </router-link>
            <router-link v-if="!isTeacher" to="/ask-ai" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-robot"></i>  Ask AI
            </router-link>
            <router-link v-if="!isTeacher" to="/materials" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-journal-text"></i>  Course Materials
            </router-link>
            <router-link v-if="isTeacher" to="/teacher-ask-ai" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-robot"></i>  Ask AI
            </router-link>
            <router-link v-if="isTeacher" to="/queue" class="nav-link border mb-2" active-class="active">
                <i class="bi bi-list-task"></i>  Queue & Workers
            </router-link>
            <router-link v-if="isTeacher" to="/global_files" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-folder"></i>  Global Files
            </router-link>
            <router-link v-if="isTeacher" to="/course_actions" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-gear"></i>  Course Actions
            </router-link>
            <router-link v-if="isTeacher" to="/semesters" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-calendar3"></i>  Manage Semesters
            </router-link>
            <router-link v-if="isTeacher" to="/course_materials" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-journal-text"></i>  Course Materials
            </router-link>
            <router-link to="/competition" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-trophy"></i>  Competition
            </router-link>
            <router-link to="/help" class="nav-link border mb-2" active-class="active">
              <i class="bi bi-question-circle"></i>  Help
            </router-link>
          </nav>
        </aside>

        <!-- Center Content -->
        <main class="col-md-8 col-sm-6 py-3">
          <slot></slot>
        </main>

        <!-- Right Sidebar -->
        <aside class="col-md-2 col-sm-3 p-3 border-start">
          <slot name="right-sidebar"></slot>
        </aside>
      </div>
    </div>

    <!-- Footer -->
    <footer class="text-center p-1 mt-auto text-white w-100" style="background-color: #4b6178;">
      <div>© 2025 PA036 - Submission and Task Evaluation Manager</div>
    </footer>
  </div>

  <StemAlert ref="stemAlert"/>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import AuthenticationController from '@/controllers/AuthenticationController';
import StemAlert from './StemAlert.vue';
import { Modal, Offcanvas } from "bootstrap";

export default {
  components: {
    StemAlert,
  },
  props: {
    title: {
      type: String,
      required: true,
    },
    hideNavigation: {
      type: Boolean,
      default: false,
    },
    hideImpersonation: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      impersonateUco: ''
    }
  },
  computed: {
    ...mapState(["theme"]),
    ...mapGetters(['userUco', 'isTeacher', 'timeToExpire', 'isImpersonating']),
    // Runtime env label served per k8s release via /env.js (window.__ENV__.label), so one image can
    // render a distinct banner per env. Falls back to the build-time VUE_APP_PRODUCTION flag used by
    // the docker-compose preprod build. Empty/undefined => production styling.
    envLabel() {
      return (typeof window !== 'undefined' && window.__ENV__ && window.__ENV__.label) || '';
    },
    isNonProdEnv() {
      return this.envLabel !== '' || process.env.VUE_APP_PRODUCTION === "False";
    },
    headerBrand() {
      if (this.envLabel) return 'PA036-' + this.envLabel.toUpperCase();
      return process.env.VUE_APP_PRODUCTION === "False" ? 'PA036-PREPROD' : 'PA036';
    },
    headerStyle() {
      return {
        backgroundColor: this.isNonProdEnv ? '#682e8a' : '#002776',
      };
    },
    refresh() {
      return this.$store.state.refresh;
    },
    isExpired() {
      return this.timeToExpire <= 0;
    },
    formattedTimeToExpire() {
      this.refresh;
      if (this.timeToExpire <= 0) {
        return 'Expired';
      }
      const minutes = Math.floor(this.timeToExpire / 1000 / 60);
      return `${minutes} min`;
    }
  },
  mounted() {
    document.documentElement.setAttribute("data-bs-theme", this.theme);
  },
  watch: {
    theme(newTheme) {
      document.documentElement.setAttribute("data-bs-theme", newTheme);
    }
  },
  methods: {
    ...mapActions(["toggleTheme", "impersonateStudent", "stopImpersonation"]),
    // Close offcanvas menu
    closeOffcanvasMenu() {
      const offcanvasEl = document.getElementById('offcanvasMenu');
      const offcanvasInstance = Offcanvas.getInstance(offcanvasEl);
      if (offcanvasInstance) offcanvasInstance.hide();
    },
    openImpersonateModal() {
      const modalEl = document.getElementById('impersonateModal');
      let modalInstance = Modal.getInstance(modalEl);
      if (!modalInstance) {
        modalInstance = new Modal(modalEl);
      }
      modalInstance.show();
    },
    closeImpersonateModal() {
      const modalEl = document.getElementById('impersonateModal');
      const modalInstance = Modal.getInstance(modalEl);
      if (modalInstance) {
        modalInstance.hide();
      }
      this.impersonateUco = '';
    },
    async impersonate() {
      if (!this.impersonateUco.trim()) return;
      await this.impersonateStudent(this.impersonateUco);
      this.closeImpersonateModal();
    },
    async stopImpersonationAction() {
      await this.stopImpersonation();
    },
    async logout() {
      await AuthenticationController.logout();
      this.$router.push('/login');
    }
  },
};
</script>

<style scoped>
  footer {
    background-color: #4b6178;
  }

  .nav-link.active {
    background-color: #2e3b4e !important; /* overrides the default Bootstrap styles */
    color: white !important;
  }
</style>